import { SearchModes } from '@/domainLogic/index.ts';
import type { TranslationFunction } from '@/app/components/translations/Translation.tsx';
import type { SearchInfoData } from '@/app/search/types.ts';
import type { CommonData } from '@/app/common/createCommonData.ts';
import { getIsLoyaltyEnabled } from '@/app/common/Loyalty/utils.ts';
import type { DealCard } from '../../../utils/buildDealCards/dealCardTypes';
import type { Localization } from '../../../common/Localization';
import { decorateInventoryAge, getPriceLabel } from '../../../utils/buildDealCards/utils';
import { formatPrice } from '../../../utils/formatting';
import { decorateProduct } from './getDecorators';
import { replaceVar } from '../../../utils/misc';

export type GetPriceDetailsProps = {
  dealCard: DealCard;
  searchInfoData: SearchInfoData;
  localization: Localization;
  commonData?: CommonData;
  // currency: Currency;
  t: TranslationFunction;
  isMobile: boolean;
  isOverlay: boolean;
};

const MIN_PERCENTAGE_MYPRICE_DISCOUNT = 3;

export type PriceDetails = {
  productLabel: string | null;
  price: string;
  priceLabel: string;
  priceBeforeDiscount: string | null;
  discountPercentage: string | null;
  totalPrice: string;
  totalPriceLabel: string;
  isHoOneNightStay: boolean;
  showDiscount: boolean;
  totalPerNightsLabel: string | null;
};

export const getPercentage = (partialValue: number, totalValue: number) =>
  partialValue && totalValue ? Math.round((100 * partialValue) / totalValue) : 0;

export const getDiscountPercentageWithMin = (partialValue: number, totalValue: number, minPercentage: number) => {
  if (!totalValue || !partialValue || totalValue <= 0 || partialValue <= 0) return null;

  const percentage = getPercentage(totalValue - partialValue, totalValue);
  return percentage > minPercentage ? `-${percentage}%` : null;
};

export const getDiscountPercentage = (partialValue: number, totalValue: number): number | null => {
  if (!totalValue || !partialValue || totalValue <= 0 || partialValue <= 0) return null;

  return getPercentage(totalValue - partialValue, totalValue);
};

export const PriceIsLowerThanOriginalPrice = (price: number, originalPrice: number): boolean => price < originalPrice;

const getDealPrice = ({ dealCard, localization }: { dealCard: DealCard; localization: Localization }): string => {
  const price = dealCard?.product?.rate?.price?.price;

  return formatPrice({ price, localization });
};
export const getTotalPrice = (dealCard: DealCard, localization: Localization) => {
  const price = dealCard?.product?.rate?.price?.total?.price;

  return formatPrice({ price, localization });
};

const getDaysAmount = (dateTo: string, dateFrom: string) =>
  (new Date(dateTo).valueOf() - new Date(dateFrom).valueOf()) / (1000 * 60 * 60 * 24);

// const getDailyPrice = ({
//   dealCard,
//   locale,
//   currency,
// }: {
//   dealCard: DealCard;
//   locale: string;
//   currency: Currency;
// }): string => {
//   const totalPrice = dealCard?.product?.rate?.price?.total?.price;
//   const dateFrom = dealCard?.product?.dates?.dateFrom;
//   const dateTo = dealCard?.product?.dates?.dateTo;
//   const roomsCount = dealCard?.product?.rate?.features?.rooms;
//
//   const days = dateTo && dateFrom ? getDaysAmount(dateTo, dateFrom) : 1;
//   const dailyPrice = totalPrice / days / roomsCount;
//
//   return formattedPrice(dailyPrice, locale, currency);
// };

export const getDailyStrikethroughPrice = (dealCard: DealCard, localization: Localization) => {
  const totalStrikethrough = dealCard?.product?.rate?.price?.total?.strikethrough;
  const dateFrom = dealCard?.product?.dates?.dateFrom;
  const dateTo = dealCard?.product?.dates?.dateTo;
  const roomsCount = dealCard?.product?.rate?.features?.rooms;
  const days = dateTo && dateFrom ? getDaysAmount(dateTo, dateFrom) : 1;

  const dailyPriceBeforeDiscount = totalStrikethrough / days / roomsCount;

  return formatPrice({ price: dailyPriceBeforeDiscount, localization });
};

export const getStrikethroughPrice = (dealCard: DealCard, localization: Localization): string | null => {
  const { price, strikethrough } = dealCard.product.rate.price;

  if (PriceIsLowerThanOriginalPrice(price, strikethrough)) return formatPrice({ price: strikethrough, localization });
  return null;
};

export const getShowDiscount = (dealCard: DealCard, isPerPerson?: boolean) => {
  const price = dealCard?.product?.rate?.price?.price;
  const strikethrough = dealCard?.product?.rate?.price?.strikethrough;
  const totalPrice = dealCard?.product?.rate?.price?.total?.price;
  const totalStrikethrough = dealCard?.product?.rate?.price?.total?.strikethrough;

  if (isPerPerson) return PriceIsLowerThanOriginalPrice(price, strikethrough);
  return PriceIsLowerThanOriginalPrice(totalPrice, totalStrikethrough);
};

export const getDealCardMyPriceDiscountPercentage = (dealCard: DealCard, isPerPerson?: boolean): string | null => {
  const price = dealCard?.product?.rate?.price?.price;
  const strikethrough = dealCard?.product?.rate?.price?.strikethrough;
  const totalPrice = dealCard?.product?.rate?.price?.total?.price;
  const totalStrikethrough = dealCard?.product?.rate?.price?.total?.strikethrough;

  if (isPerPerson) return getDiscountPercentageWithMin(price, strikethrough, MIN_PERCENTAGE_MYPRICE_DISCOUNT);
  return getDiscountPercentageWithMin(totalPrice, totalStrikethrough, MIN_PERCENTAGE_MYPRICE_DISCOUNT);
};

export const getDealCardDiscountPercentage = (dealCard: DealCard, isPerPerson?: boolean): number | null => {
  const price = dealCard?.product?.rate?.price?.price;
  const strikethrough = dealCard?.product?.rate?.price?.strikethrough;
  const totalPrice = dealCard?.product?.rate?.price?.total?.price;
  const totalStrikethrough = dealCard?.product?.rate?.price?.total?.strikethrough;

  if (isPerPerson) return getDiscountPercentage(price, strikethrough);
  return getDiscountPercentage(totalPrice, totalStrikethrough);
};

export const getDealCardDiscountPercentageLabel = (dealCard: DealCard, isPerPerson?: boolean): string | null => {
  const percentage = getDealCardDiscountPercentage(dealCard, isPerPerson);
  return (percentage ?? 0) > 0 ? `-${percentage}%` : null;
};

export const getPrice = ({
  dealCard,
  // searchMode,
  localization,
}: {
  dealCard: DealCard;
  searchMode: SearchModes;
  localization: Localization;
}): string =>
  // const isPerPerson = dealCard?.product?.rate?.price?.isPerPerson;
  // const showDailyPrice = searchMode === SearchModes.HO;
  //
  // if (showDailyPrice && !isPerPerson) {
  //   return getDailyPrice({ dealCard, localization });
  // }
  getDealPrice({ dealCard, localization });

export const decoratePriceAge = (priceFound: string, language: string, age?: number): string => {
  if (!age || age <= 30) return '';
  const { unit, time } = decorateInventoryAge(age, language);
  return replaceVar(priceFound.replace(/__(.*?)__/g, '{$1}'), { unit, time });
};

const getPriceBeforeDiscount = ({
  // isPerPerson,
  dealCard,
  localization,
}: {
  // isPerPerson: boolean;
  dealCard: DealCard;
  localization: Localization;
}) =>
  //   isPerPerson
  //     ? getStrikethroughPrice(dealCard, localization)
  //     : getDailyStrikethroughPrice(dealCard, localization);
  getStrikethroughPrice(dealCard, localization);

const getProductLabel = ({ searchMode, t }: { searchMode: SearchModes; t: TranslationFunction }) =>
  searchMode === SearchModes.DP ? decorateProduct({ searchMode, t }) : null;

const getTotalPriceLabel = ({ totalPrice, t }: { totalPrice: string; t: TranslationFunction }) =>
  `${totalPrice} ${t('vacation.oss.cards.total')}`;

export const getTotalPerNightsLabel = ({ nights, t }: { nights?: number; t: TranslationFunction }) => {
  if (!nights) return null;
  if (nights === 1) return t('vacation.oss.cards.total_for_night');
  return t('vacation.oss.cards.total_for_nights', { number: `${nights}` });
};

export const getPriceDetails = ({
  dealCard,
  // commonData,
  searchInfoData,
  localization,
  isMobile,
  isOverlay,
  t,
}: GetPriceDetailsProps): PriceDetails => {
  const { searchMode } = searchInfoData;
  const rooms = dealCard?.product?.rate?.features?.rooms;
  const discountEnabled = dealCard?.product?.rate?.price?.showDiscount;
  const isPerPerson = dealCard?.product?.rate?.price?.isPerPerson;
  const isHO = searchMode === SearchModes.HO;
  const hasMultipleRooms = !!(rooms && rooms > 1);
  const isMobileOrOverlay = isMobile || isOverlay;
  const isHoOneNightStay = dealCard?.product?.dates?.nights === 1 && !hasMultipleRooms && searchMode === SearchModes.HO;
  const productLabel = getProductLabel({ searchMode, t });
  const price = getPrice({ dealCard, searchMode, localization });
  const hasMyPrice = dealCard?.product?.rate?.flags?.myPrice;
  const loyaltyEnabled = getIsLoyaltyEnabled(localization.configValuesApp);
  const priceLabel = getPriceLabel({
    t,
    isMobileOrOverlay,
    isPerPerson,
    isDesktop: true,
    hasMultipleRooms,
    isHoOneNightStay,
  });
  const showDiscount = discountEnabled && getShowDiscount(dealCard, isPerPerson);
  const priceBeforeDiscount = getPriceBeforeDiscount({ dealCard, localization });
  const totalPrice = getTotalPrice(dealCard, localization);
  const totalPriceLabel = getTotalPriceLabel({ totalPrice, t });
  const showDiscountPercentage = showDiscount && (hasMyPrice || loyaltyEnabled);
  const discountPercentage = getDealCardDiscountPercentageLabel(dealCard);
  const showTotalPerNightsLabel = isHO;
  const totalPerNightsLabel = getTotalPerNightsLabel({ nights: dealCard?.product?.dates?.nights, t });

  return {
    isHoOneNightStay,
    productLabel,
    price,
    priceLabel,
    priceBeforeDiscount: showDiscount ? priceBeforeDiscount : null,
    totalPrice,
    totalPriceLabel,
    showDiscount,
    discountPercentage: showDiscountPercentage ? discountPercentage : null,
    totalPerNightsLabel: showTotalPerNightsLabel ? totalPerNightsLabel : null,
  };
};
